import React from "react";
import * as ReactDOM from "react-dom/client";
import "./landing-shared.entry.jsx";
const SiteNav = globalThis.SiteNav ?? globalThis.window?.SiteNav;
const SiteFooter = globalThis.SiteFooter ?? globalThis.window?.SiteFooter;
// Exit Nodes page
const { useState } = React;

const Arrow = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square"/>
  </svg>
);

function Hero() {
  return (
    <section className="section section-dark ex-hero">
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <span className="ex-eyebrow">
          <span className="eb-dot"></span>EXIT NODES · TRUSTED-DEVICE EGRESS
          <span className="pill-beta">BUSINESS</span>
        </span>
        <h1>Outbound through<br/>a device you<br/><em>already trust.</em></h1>
        <p className="lead">Route selected traffic through a device your workspace already trusts. Home lab, office machine, or a chosen server &mdash; framed as controlled trusted egress, not a consumer browsing VPN.</p>

        <div className="ex-hero-bottom">
          <div className="map-viz">
            <div className="vlabel">EGRESS · TRAFFIC ROUTING</div>
            <div className="vstatus"><span className="live"></span>2 EXITS · ACTIVE</div>

            <div className="node" style={{ top: 80, left: 28 }}>
              <div className="nlbl">CLIENT · BERLIN</div>
              <div className="nname">macbook-aria</div>
              <div className="nip">100.64.12.4</div>
            </div>

            <div className="node" style={{ top: 200, left: 28 }}>
              <div className="nlbl">CLIENT · NEW YORK</div>
              <div className="nname">desktop-jules</div>
              <div className="nip">100.64.12.7</div>
            </div>

            <div className="node active" style={{ top: 80, right: 28 }}>
              <div className="nlbl">EXIT · LONDON OFFICE</div>
              <div className="nname">office-bastion</div>
              <div className="nip">82.71.144.18</div>
            </div>

            <div className="node active" style={{ top: 200, right: 28 }}>
              <div className="nlbl">EXIT · HOME LAB</div>
              <div className="nname">homelab-attic</div>
              <div className="nip">88.214.5.221</div>
            </div>

            {/* Connection lines */}
            <svg style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} width="100%" height="100%">
              <defs>
                <marker id="arr" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto">
                  <path d="M0 0 L8 4 L0 8 z" fill="var(--accent)"/>
                </marker>
              </defs>
              <line x1="22%" y1="29%" x2="62%" y2="29%" stroke="var(--accent)" strokeWidth="1" strokeDasharray="3 3" markerEnd="url(#arr)"/>
              <line x1="22%" y1="58%" x2="62%" y2="58%" stroke="var(--accent)" strokeWidth="1" strokeDasharray="3 3" markerEnd="url(#arr)"/>
            </svg>

            <div style={{ position: 'absolute', bottom: 24, left: 28, right: 28, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-dark-muted)', letterSpacing: '0.04em', display: 'flex', justifyContent: 'space-between' }}>
              <span>2 active rules · 14.2 MB / hr</span>
              <span>handshake p50 · 38 ms</span>
            </div>
          </div>

          <div className="ex-stats">
            <div className="ex-stat"><div className="big"><em>per-device</em></div><div className="lbl">Eligibility is managed from the dashboard today</div></div>
            <div className="ex-stat"><div className="big"><em>4</em> policies</div><div className="lbl">routes_only, split_tunnel, all_traffic, disabled</div></div>
            <div className="ex-stat"><div className="big"><em>WireGuard</em></div><div className="lbl">Standard protocol, kernel-fast</div></div>
            <div className="ex-stat"><div className="big"><em>Visible</em></div><div className="lbl">History, warnings, and current sessions in the dashboard</div></div>
          </div>
        </div>

        <div style={{ marginTop: 56, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Create a Business workspace <Arrow /></span></a>
          <a href="#workflow" className="btn btn-on-dark btn-lg"><span>How it works</span></a>
        </div>
      </div>
    </section>
  );
}

function Workflow() {
  const steps = [
    { n: '01', t: 'Pick a device.', b: 'Choose a trusted device in your workspace that is eligible to act as an exit. The current surface is workspace-centric, not a giant public exit catalog.' },
    { n: '02', t: 'Mark it eligible.', b: 'The current product lets owners or admins enable or disable exit-node eligibility from the dashboard, with confirmation and plain-language risk copy.' },
    { n: '03', t: 'Choose a policy.', b: 'Attach the client device to the exit using one of the current policy modes: <code>routes_only</code>, <code>split_tunnel</code>, <code>all_traffic</code>, or <code>disabled</code>.' },
    { n: '04', t: 'Verify and manage.', b: 'The dashboard shows current sessions, recent history, and support notes. The current lifecycle is auditable, and <code>disabled</code> is the current stop-with-history state.' },
  ];
  return (
    <section className="wf-light" id="workflow">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">How it works</span>
          <h2>Four steps. About ninety seconds.</h2>
          <p>You already trust some of your devices more than the open internet. This is how you make that opinion executable.</p>
        </div>
        <div className="wf-grid">
          {steps.map(s => (
            <div key={s.n} className="wf-step">
              <div className="num">STEP {s.n}</div>
              <h3>{s.t}</h3>
              <p dangerouslySetInnerHTML={{ __html: s.b }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function UseCases() {
  const cases = [
    { n: '01', t: 'Reach &ldquo;office-only&rdquo; tools from anywhere', b: 'Internal Jenkins, the staging environment behind a corporate firewall, the legacy admin panel that allowlists 82.71.144.18 and only 82.71.144.18.', src: 'macbook-aria', dst: 'office-bastion' },
    { n: '02', t: 'Pin a service to your home or office IP', b: 'Some banks, partner portals, and older enterprise apps still decide who you are by your outbound IP. Use an IP from infrastructure you control.', src: 'macbook-aria', dst: 'homelab-attic' },
    { n: '03', t: 'Egress from a chosen jurisdiction', b: 'Send a subset of traffic through a trusted device in the country you actually operate in &mdash; for compliance testing, region checks, or office-IP routing.', src: 'desktop-jules', dst: 'eu-frankfurt' },
    { n: '04', t: 'Give a contractor a stable IP', b: 'They keep their laptop. Their outbound for your apps comes from the box you control. Onboarding takes one minute and offboarding takes one second.', src: 'contractor-laptop', dst: 'office-bastion' },
    { n: '05', t: 'Test geo behaviour on infrastructure you trust', b: 'Use a device you control in the target region when you need to see how your app behaves there, then remove or disable the session when the test is done.', src: 'desktop-jules', dst: 'apac-singapore' },
    { n: '06', t: 'A controlled split-tunnel setup', b: 'Use the current split-tunnel or all-traffic policies when a trusted-device route is genuinely the right answer, while keeping the lifecycle visible in the dashboard.', src: 'macbook-aria', dst: 'mixed' },
  ];
  return (
    <section className="ux-section">
      <div className="dark-grid"></div>
      <div className="container">
        <div className="section-h">
          <span className="section-label">Six honest jobs it does</span>
          <h2 style={{ color: 'var(--text-dark-primary)' }}>What people actually use this for.</h2>
          <p>If your job is on this list, exit nodes will feel obvious. If it isn&rsquo;t, you might want Controlled Gateway instead.</p>
        </div>
        <div className="ux-grid">
          {cases.map((c, i) => (
            <div key={i} className="ux">
              <div className="tile">{c.n}</div>
              <h4 dangerouslySetInnerHTML={{ __html: c.t }} />
              <p dangerouslySetInnerHTML={{ __html: c.b }} />
              <div className="ipsim">
                <span>{c.src}</span>
                <strong>→ {c.dst}</strong>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Rules() {
  return (
    <section className="rules-section">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">Routing rules</span>
          <h2>Which traffic, which exit, which time.</h2>
          <p>The current product supports explicit exit-session policies from the dashboard. The rule sketch below is representative product direction, not a promise that every advanced selector is already polished and self-serve today.</p>
        </div>
        <div className="rules-grid">
          <div className="rules-copy">
            <h3>Start with the current policy model.</h3>
            <p>What we can honestly claim today is the dashboard-managed exit-session flow: choose a client device, choose an eligible exit device, and save <code>routes_only</code>, <code>split_tunnel</code>, <code>all_traffic</code>, or <code>disabled</code>. More expressive rule-builder ergonomics belong in future polish, not in today&rsquo;s public promise.</p>
            <ul>
              <li><code>routes_only</code> keeps the trusted exit selected without forcing a default-route takeover</li>
              <li><code>split_tunnel</code> applies explicit included CIDRs</li>
              <li><code>all_traffic</code> routes default traffic through the selected exit</li>
              <li><code>disabled</code> is the current audit-preserving stop state</li>
              <li>Current session rows link back to the real controls used to manage them</li>
            </ul>
          </div>

          <div className="rules-card">
            <div className="rh">
              <span>exit-session policies</span>
              <span>4 MODES · 2 EXITS</span>
            </div>
            <div className="rule-row">
              <div className="rule-l"><span>POLICY</span> routes_only</div>
              <div className="rule-r">office-bastion</div>
            </div>
            <div className="rule-row">
              <div className="rule-l"><span>POLICY</span> split_tunnel · 10.40.0.0/16</div>
              <div className="rule-r">office-bastion</div>
            </div>
            <div className="rule-row">
              <div className="rule-l"><span>POLICY</span> all_traffic</div>
              <div className="rule-r">homelab-attic</div>
            </div>
            <div className="rule-row">
              <div className="rule-l"><span>STATUS</span> disabled</div>
              <div className="rule-r">eu-frankfurt</div>
            </div>
            <div className="rule-row">
              <div className="rule-l"><span>VIEW</span> session history + warnings</div>
              <div className="rule-r" style={{ color: 'var(--text-light-muted)', borderColor: 'var(--border-light)' }}>direct</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SupportBoundary() {
  return (
    <section className="rules-section">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">Support boundary</span>
          <h2>What the current release supports.</h2>
          <p>Exit Nodes are for Business workspaces that want trusted-device egress through their own enrolled infrastructure.</p>
        </div>
        <div className="rules-grid">
          <div className="rules-copy">
            <h3>Supported host and permission model.</h3>
            <p>Owners and admins can enable an enrolled Linux device as an exit node and attach client devices to it. The current supported exit-host set is Ubuntu 24.04 LTS, Debian 12, and AlmaLinux 9 on systemd hosts.</p>
            <ul>
              <li>Ubuntu 26.04 is not supported until a real host passes the full live matrix</li>
              <li>Members cannot enable exit eligibility or create active exit-node sessions unless they have an admin role</li>
              <li>Disable operations remain available so a workspace can unwind exit-node state safely</li>
              <li>Unused setup tokens can be revoked, and disabling a device stops related exit-node sessions</li>
            </ul>
          </div>

          <div className="rules-copy">
            <h3>Routing, DNS, and limits.</h3>
            <p><code>all_traffic</code> sends the client IPv4 default route through the selected exit while preserving named.network control-plane reachability. Supported hosts passed DNS and IPv6 leak checks; unsupported direct IPv6 egress fails closed instead of silently bypassing the tunnel.</p>
            <ul>
              <li>Use <code>split_tunnel</code> for explicit included CIDRs</li>
              <li>Use <code>disabled</code> to stop egress while keeping session history</li>
              <li>SMTP-class forwarding is blocked by default on Linux exit nodes</li>
              <li>No anonymity, shared public exit pool, streaming-unblock, or desktop kill-switch promise is made here</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Honest() {
  const cards = [
    { tag: 'GOOD AT', t: 'Trusted egress for your stuff.', b: 'A device you control or your team controls. The mesh treats it as a first-class peer, with health, visible history, and policy state.' },
    { tag: 'GOOD AT', t: 'Stable outbound IPs.', b: 'When some upstream service expects you from a specific IP, you give them that IP. No more Slack messages asking IT to update the allowlist.' },
    { tag: 'GOOD AT', t: 'Selective routing.', b: 'Most of your traffic can stay direct. Only the things that need a trusted exit get one, using the current session-policy model instead of a broad consumer VPN abstraction.' },
    { tag: 'BAD AT', t: 'Anonymising you on the open internet.', b: 'This is not Tor. The exit is a device with a known IP that someone you know operates. If you need privacy from a determined adversary, this is the wrong tool.' },
    { tag: 'BAD AT', t: 'Bypassing TOS-style geo blocks.', b: 'This is not the product story we want to lead with. Exit Nodes are about trusted-device routing for real workspace needs, not a public geo-evasion catalog.' },
    { tag: 'BAD AT', t: 'Big shared pools.', b: 'There are no giant shared exit pools here. Every exit is a device your workspace explicitly marks as eligible. By design.' },
  ];
  return (
    <section className="honest">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">Honestly</span>
          <h2>What this is good at, what it isn&rsquo;t.</h2>
          <p>The honest framing &mdash; before you read further or start a trial.</p>
        </div>
        <div className="honest-grid">
          {cards.map((c, i) => (
            <div key={i} className="honest-card">
              <div className="tag">{c.tag}</div>
              <h4>{c.t}</h4>
              <p>{c.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = [
    { q: 'Is this the same as a "VPN exit node"?', a: 'Conceptually similar, but the public claim here is narrower. Exit Nodes are a trusted-device egress control surface for Business workspaces, not a mass-market browsing VPN.' },
    { q: 'Is it Tor?', a: 'No. Tor is built for anonymity against determined adversaries through traffic mixing. Exit nodes here are about routing through someone you trust, with a fixed and known IP. Different threat model entirely.' },
    { q: 'How is it different from Controlled Gateway?', a: 'Exit Nodes pin egress to a device you operate &mdash; your IP, your responsibility. Controlled Gateway is a managed, audited proxy with caps and allowlists for outbound to the open internet. Use Exit Nodes for &ldquo;reach my office&rdquo;. Use Gateway for &ldquo;run an audited script that talks to a partner API&rdquo;.' },
    { q: 'Will the exit owner see my traffic?', a: 'Treat the exit owner as trusted infrastructure. The public product claim should stay conservative here: this is trusted-device routing, not anonymity tooling.' },
    { q: 'What happens if the exit goes offline?', a: 'The current product exposes session state, warnings, and history so operators can tell when an exit path is degraded or disabled. This page should avoid inventing more detailed fallback semantics than we have publicly locked down.' },
    { q: 'How fast is it?', a: 'WireGuard-backed trusted egress is real, but we should not turn this page into a public performance-contract page. The important public truth today is policy control plus visible diagnostics, not benchmark marketing.' },
    { q: 'Can I use a $5 VPS as an exit?', a: 'A trusted server you control is a plausible use case, but the product promise we should stand behind today is the dashboard-managed trusted-device egress flow rather than a specific one-command VPS recipe.' },
    { q: 'Which hosts are supported?', a: 'The current support promise is Ubuntu 24.04 LTS, Debian 12, and AlmaLinux 9 on Linux systemd hosts. Ubuntu 26.04 is intentionally excluded until it passes the same live install, routing, DNS, reboot, and update matrix.' },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="section section-light">
      <div className="container-narrow">
        <div className="section-h">
          <span className="section-label on-light">FAQ</span>
          <h2>Eight common questions.</h2>
          <p>Anything not covered? Email us &mdash; we&rsquo;ll add it here.</p>
        </div>
        <div className="faq-list">
          {faqs.map((f, i) => (
            <div key={i} className={`faq-item ${open === i ? 'open' : ''}`}>
              <button className="faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
                <span>{f.q}</span><span className="plus">+</span>
              </button>
              <div className="faq-a" dangerouslySetInnerHTML={{ __html: f.a }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="section section-dark" style={{ padding: '128px 0' }}>
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <div className="cta-final">
          <span className="section-label">Business · trusted egress</span>
          <h2>Pin some traffic.<br/><em>Through a device you trust.</em></h2>
          <p>Exit Nodes are an advanced Business surface for trusted-device egress, not a baseline free-plan browsing promise.</p>
          <div className="ctas" style={{ marginTop: 36 }}>
            <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Create a Business workspace <Arrow /></span></a>
            <a href="/contact" className="btn btn-on-dark btn-lg"><span>Talk to us first</span></a>
          </div>
        </div>
      </div>
    </section>
  );
}

function App() {
  return (
    <>
      <SiteNav />
      <Hero />
      <Workflow />
      <UseCases />
      <Rules />
      <SupportBoundary />
      <Honest />
      <FAQ />
      <CTA />
      <SiteFooter accent="exit-nodes" />
    </>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);

